/**
 * AssistantOnboardingWizard.tsx
 * 新規アシスタント導入ウィザード（6ステップ）
 *
 * Step 1: 基本情報（氏名・メール・電話・LINE）
 * Step 2: アカウント設定（ログイン有効化・初期パスワード生成）
 * Step 3: 勤務・給与設定（役割・日給・研修設定）
 * Step 4: 日報・シフト設定
 * Step 5: 初回案内文の確認・コピー
 * Step 6: 確認・登録実行
 */

import { useState } from 'react'
import { api } from '../api/client'
import { todayJst } from '../utils/jst'

// ============================================================
// 型定義
// ============================================================
type Step = 1 | 2 | 3 | 4 | 5 | 6

interface WizardData {
  // Step 1
  name: string
  display_name: string
  email: string
  phone: string
  contact_note: string
  notes: string
  // Step 2
  login_enabled: boolean
  generate_password: boolean
  must_change_password: boolean
  initial_password: string
  // Step 3
  role_type: string
  joined_at: string
  daily_rate: string
  training_enabled: boolean
  training_daily_rate: string
  training_days_required: string
  transport_cap: string
  overtime_hourly: string
  payroll_enabled: boolean
  onboarding_note: string
  // Step 4
  report_enabled: boolean
  shift_enabled: boolean
}

// ============================================================
// ヘルパー
// ============================================================

/** ランダムな初期パスワードを生成 */
function genPassword(): string {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const arr = crypto.getRandomValues(new Uint8Array(10))
  return Array.from(arr).map((b) => chars[b % chars.length]).join('')
}

/** @mangaos.test の仮メールかどうか */
function isProvisionalEmail(email: string): boolean {
  return email.toLowerCase().endsWith('@mangaos.test')
}

/** ログインURL（フロントエンドのオリジン + /login） */
const LOGIN_URL = window.location.origin + '/login'

/** LINE / メール用の初回案内文を生成 */
function buildWelcomeMessage(data: WizardData): string {
  const hasPassword = data.login_enabled && data.generate_password && data.initial_password
  return [
    `${data.name}さん`,
    '',
    'お疲れさまです。',
    'MangaOSのアシスタント用アカウントを作成しました。',
    '',
    '以下よりログインをお願いいたします。',
    '',
    `ログインURL：${LOGIN_URL}`,
    `メールアドレス：${data.email}`,
    `初期パスワード：${hasPassword ? data.initial_password : '（別途お伝えします）'}`,
    '',
    'ログイン後、日報入力・シフト確認・給与報告の確認ができます。',
    '初回ログイン後、必要に応じてパスワード変更をお願いいたします。',
    '',
    'どうぞよろしくお願いいたします。',
  ].join('\n')
}

// ============================================================
// メインコンポーネント
// ============================================================
export function AssistantOnboardingWizard({
  onClose,
  onSaved,
}: {
  onClose: () => void
  onSaved: (name: string) => void
}) {
  const today = todayJst()

  const [step, setStep] = useState<Step>(1)
  const [data, setData] = useState<WizardData>({
    name: '', display_name: '', email: '', phone: '', contact_note: '', notes: '',
    login_enabled: true, generate_password: true, must_change_password: true,
    initial_password: genPassword(),
    role_type: 'general', joined_at: today,
    daily_rate: '12000', training_enabled: false, training_daily_rate: '10000',
    training_days_required: '30', transport_cap: '8000', overtime_hourly: '1200',
    payroll_enabled: true, onboarding_note: '',
    report_enabled: true, shift_enabled: true,
  })
  const [errors,   setErrors]   = useState<Record<string, string>>({})
  const [saving,   setSaving]   = useState(false)
  const [copyDone, setCopyDone] = useState<string | null>(null)
  const [done, setDone] = useState<{
    assistant_id: string
    login_created: boolean
  } | null>(null)

  const set = <K extends keyof WizardData>(key: K, value: WizardData[K]) => {
    setData((d) => ({ ...d, [key]: value }))
    setErrors((e) => { const n = { ...e }; delete n[key as string]; return n })
  }

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopyDone(label)
    setTimeout(() => setCopyDone(null), 2000)
  }

  // ステップごとのバリデーション
  const validateStep = (s: Step): Record<string, string> => {
    const e: Record<string, string> = {}
    if (s === 1) {
      if (!data.name.trim())  e.name  = '氏名は必須です'
      if (!data.email.trim()) e.email = 'メールアドレスは必須です'
      else if (!data.email.includes('@')) e.email = 'メールアドレスの形式が正しくありません'
    }
    if (s === 3) {
      if (isNaN(Number(data.daily_rate))          || Number(data.daily_rate)          < 0)  e.daily_rate = '0以上を入力してください'
      if (isNaN(Number(data.training_daily_rate)) || Number(data.training_daily_rate) < 0)  e.training_daily_rate = '0以上を入力してください'
      if (isNaN(Number(data.transport_cap))       || Number(data.transport_cap)       < 0)  e.transport_cap = '0以上を入力してください'
      if (data.training_enabled && (isNaN(Number(data.training_days_required)) || Number(data.training_days_required) < 1))
        e.training_days_required = '1以上を入力してください'
    }
    return e
  }

  const goNext = () => {
    const e = validateStep(step)
    if (Object.keys(e).length > 0) { setErrors(e); return }
    setStep((s) => (Math.min(6, s + 1)) as Step)
  }
  const goBack = () => setStep((s) => (Math.max(1, s - 1)) as Step)

  const handleSubmit = async () => {
    setSaving(true)
    setErrors({})
    try {
      const result = await api.assistants.onboarding({
        name:                data.name.trim(),
        display_name:        data.display_name.trim() || undefined,
        email:               data.email.trim().toLowerCase(),
        phone:               data.phone.trim()        || undefined,
        contact_note:        data.contact_note.trim() || undefined,
        notes:               data.notes.trim()        || undefined,
        role_type:           data.role_type,
        joined_at:           data.joined_at,
        daily_rate:          Number(data.daily_rate),
        training_enabled:    data.training_enabled ? 1 : 0,
        training_daily_rate: Number(data.training_daily_rate),
        training_days_required: Number(data.training_days_required),
        transport_cap:       Number(data.transport_cap),
        overtime_hourly:     Number(data.overtime_hourly),
        payroll_enabled:     data.payroll_enabled ? 1 : 0,
        shift_enabled:       data.shift_enabled   ? 1 : 0,
        report_enabled:      data.report_enabled  ? 1 : 0,
        login_enabled:       data.login_enabled   ? 1 : 0,
        initial_password:    data.login_enabled && data.generate_password
          ? data.initial_password : undefined,
        must_change_password: data.must_change_password ? 1 : 0,
        onboarding_note:     data.onboarding_note.trim() || undefined,
        onboarding_status:   isProvisionalEmail(data.email) ? 'draft' : 'active',
      })
      setDone({ assistant_id: result.assistant_id, login_created: result.login_created })
      onSaved(data.name.trim())
    } catch (e: unknown) {
      setErrors({ submit: (e as Error).message ?? 'エラーが発生しました' })
    } finally {
      setSaving(false)
    }
  }

  const isProvisional = isProvisionalEmail(data.email)
  const welcomeMsg    = buildWelcomeMessage(data)

  const STEP_LABELS: Record<Step, string> = {
    1: '基本情報',
    2: 'アカウント設定',
    3: '勤務・給与',
    4: '日報・シフト',
    5: '案内文',
    6: '確認',
  }

  // ── 完了画面 ────────────────────────────────────────────
  if (done) {
    return (
      <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ maxWidth: 600 }}>
          <div className="modal-header">
            <div className="modal-title">✓ アシスタントを追加しました</div>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body">
            {/* 成功メッセージ */}
            <div style={{
              background: 'var(--green-light)', border: '1px solid rgba(45,122,58,0.3)',
              borderRadius: 8, padding: '14px 18px', marginBottom: 16,
            }}>
              <div style={{ fontWeight: 700, color: 'var(--green)', fontSize: 14, marginBottom: 4 }}>
                {data.name} さんを追加しました
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{data.email}</div>
              {isProvisional && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--amber)' }}>
                  ⚠ 仮メールアドレスで登録されています。本人のメールアドレスが確認できたら「編集」から更新してください。
                </div>
              )}
            </div>

            {/* 初期パスワード */}
            {done.login_created && data.generate_password && (
              <div style={{
                background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
                borderRadius: 8, padding: '14px 18px', marginBottom: 16,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
                  初期パスワード
                  <span style={{ fontWeight: 400, marginLeft: 8, fontSize: 11, color: 'var(--ink-3)' }}>
                    （この画面を閉じると確認できません）
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <code style={{
                    fontSize: 20, fontWeight: 700, letterSpacing: '0.12em',
                    background: 'var(--paper)', padding: '8px 14px', borderRadius: 6,
                    flex: 1, textAlign: 'center', color: 'var(--ink)',
                  }}>
                    {data.initial_password}
                  </code>
                  <button className="btn btn-outline btn-sm"
                    onClick={() => copyText(data.initial_password, 'pw')}>
                    {copyDone === 'pw' ? '✓ コピー済' : 'コピー'}
                  </button>
                </div>
              </div>
            )}

            {/* 案内文 */}
            <div style={{ marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>初回案内文（LINE・メール用）</div>
                <button className="btn btn-outline btn-sm"
                  onClick={() => copyText(welcomeMsg, 'msg')}>
                  {copyDone === 'msg' ? '✓ コピー済' : '案内文をコピー'}
                </button>
              </div>
              <pre style={{
                background: 'var(--paper-2)', borderRadius: 6, padding: '12px 14px',
                fontSize: 12, lineHeight: 1.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 260, overflowY: 'auto', color: 'var(--ink-2)',
              }}>
                {welcomeMsg}
              </pre>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={onClose}>閉じる</button>
          </div>
        </div>
      </div>
    )
  }

  // ── ウィザード本体 ───────────────────────────────────────
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>

        {/* ヘッダー */}
        <div className="modal-header">
          <div className="modal-title">新しいアシスタントを追加</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        {/* ステップインジケーター */}
        <div style={{
          display: 'flex', gap: 0, borderBottom: '1px solid var(--paper-3)',
          padding: '0 20px', overflowX: 'auto', flexShrink: 0,
        }}>
          {([1, 2, 3, 4, 5, 6] as Step[]).map((s) => {
            const isCurrent = step === s
            const isDone    = step > s
            return (
              <div key={s} style={{
                padding: '9px 10px', fontSize: 11, whiteSpace: 'nowrap',
                fontWeight: isCurrent ? 700 : 400,
                color: isCurrent ? 'var(--primary)' : isDone ? 'var(--green)' : 'var(--ink-3)',
                borderBottom: isCurrent ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: -1, cursor: 'default',
              }}>
                {isDone ? '✓' : s}. {STEP_LABELS[s]}
              </div>
            )
          })}
        </div>

        {/* ボディ */}
        <div className="modal-body">

          {/* ── Step 1: 基本情報 ── */}
          {step === 1 && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 14 }}>
                氏名とメールアドレスは必須です。メールアドレスがまだない場合は仮メール
                （例: yamada@mangaos.test）を使えます。
              </div>
              <div className="grid-2" style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label>氏名 <span style={{ color: 'var(--red)' }}>*</span></label>
                  <input value={data.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="山田 花子"
                    style={{ borderColor: errors.name ? 'var(--red)' : undefined }} />
                  {errors.name && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.name}</div>}
                </div>
                <div className="form-group">
                  <label>表示名（任意）</label>
                  <input value={data.display_name}
                    onChange={(e) => set('display_name', e.target.value)}
                    placeholder="山田さん" />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>メールアドレス <span style={{ color: 'var(--red)' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    value={data.email}
                    onChange={(e) => set('email', e.target.value)}
                    placeholder="yamada@example.com または yamada@mangaos.test"
                    style={{
                      borderColor: errors.email
                        ? 'var(--red)'
                        : isProvisional && data.email ? 'var(--amber)' : undefined,
                      paddingRight: isProvisional && data.email ? 70 : undefined,
                    }}
                  />
                  {isProvisional && data.email && (
                    <span style={{
                      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      fontSize: 10, background: 'var(--amber)', color: '#fff',
                      borderRadius: 4, padding: '2px 6px', pointerEvents: 'none',
                    }}>仮登録</span>
                  )}
                </div>
                {errors.email && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.email}</div>}
                {isProvisional && data.email && (
                  <div style={{ fontSize: 11, color: 'var(--amber)', marginTop: 4 }}>
                    このメールアドレスは仮登録用です。本人のメールアドレスが確認できたら後で更新してください。
                  </div>
                )}
              </div>

              <div className="grid-2" style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label>電話番号（任意）</label>
                  <input value={data.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    placeholder="090-1234-5678" />
                </div>
                <div className="form-group">
                  <label>LINE / 連絡先メモ（任意）</label>
                  <input value={data.contact_note}
                    onChange={(e) => set('contact_note', e.target.value)}
                    placeholder="LINE: @yamada-h" />
                </div>
              </div>

              <div className="form-group">
                <label>備考（任意）</label>
                <input value={data.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="特記事項など" />
              </div>
            </div>
          )}

          {/* ── Step 2: アカウント設定 ── */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={data.login_enabled}
                  onChange={(e) => set('login_enabled', e.target.checked)}
                  style={{ marginTop: 3 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>ログインを有効にする</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    アシスタント用アカウントを作成します。日報・シフト申請・給与確認が利用できます。
                  </div>
                </div>
              </label>

              {data.login_enabled && (
                <div style={{
                  marginLeft: 26, padding: '14px 16px',
                  background: 'var(--paper-2)', borderRadius: 8,
                  display: 'flex', flexDirection: 'column', gap: 14,
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={data.generate_password}
                      onChange={(e) => set('generate_password', e.target.checked)}
                      style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 13 }}>初期パスワードを自動生成する</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        登録後の完了画面・案内文に表示されます
                      </div>
                    </div>
                  </label>

                  {data.generate_password && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 24 }}>
                      <code style={{
                        fontSize: 16, fontWeight: 700, letterSpacing: '0.1em',
                        background: 'var(--paper)', padding: '6px 12px', borderRadius: 6, flex: 1,
                        color: 'var(--ink)',
                      }}>
                        {data.initial_password}
                      </code>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                        onClick={() => set('initial_password', genPassword())}>
                        再生成
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                        onClick={() => copyText(data.initial_password, 'pw-step2')}>
                        {copyDone === 'pw-step2' ? '✓' : 'コピー'}
                      </button>
                    </div>
                  )}

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={data.must_change_password}
                      onChange={(e) => set('must_change_password', e.target.checked)}
                      style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 13 }}>初回ログイン時にパスワード変更を促す</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>推奨設定です</div>
                    </div>
                  </label>
                </div>
              )}

              {isProvisional && (
                <div style={{
                  padding: '10px 14px',
                  background: 'var(--amber-light)', border: '1px solid rgba(176,122,18,0.25)',
                  borderRadius: 6, fontSize: 12, color: 'var(--ink-2)',
                }}>
                  ⚠ このメールアドレスは仮登録用（@mangaos.test）です。<br/>
                  ログインを有効にしても、本人のメールアドレスが確認できたら後で更新してください。
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: 勤務・給与設定 ── */}
          {step === 3 && (
            <div>
              <div className="grid-2" style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label>主担当（役割）</label>
                  <select value={data.role_type}
                    onChange={(e) => set('role_type', e.target.value)}>
                    <option value="general">全般</option>
                    <option value="background">背景</option>
                    <option value="finishing">仕上げ</option>
                    <option value="ai_art">AI作画</option>
                    <option value="research">資料</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>入社日</label>
                  <input type="date" value={data.joined_at}
                    onChange={(e) => set('joined_at', e.target.value)} />
                </div>
              </div>

              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8,
                textTransform: 'uppercase', letterSpacing: '0.06em' }}>給与設定</div>
              <div className="grid-3" style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label>基本日給（円）</label>
                  <input type="number" value={data.daily_rate}
                    onChange={(e) => set('daily_rate', e.target.value)} />
                  {errors.daily_rate && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.daily_rate}</div>}
                </div>
                <div className="form-group">
                  <label>交通費上限（円）</label>
                  <input type="number" value={data.transport_cap}
                    onChange={(e) => set('transport_cap', e.target.value)} />
                  {errors.transport_cap && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.transport_cap}</div>}
                </div>
                <div className="form-group">
                  <label>残業時給（円/時）</label>
                  <input type="number" value={data.overtime_hourly}
                    onChange={(e) => set('overtime_hourly', e.target.value)} />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={data.payroll_enabled}
                    onChange={(e) => set('payroll_enabled', e.target.checked)} />
                  <span style={{ fontWeight: 500 }}>給与・シフト計算対象にする</span>
                </label>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
                  OFFにすると月次支払計算・シフト希望の母数に含まれません（日報専用アシスタント用）
                </div>
              </div>

              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8,
                textTransform: 'uppercase', letterSpacing: '0.06em' }}>研修設定</div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={data.training_enabled}
                    onChange={(e) => set('training_enabled', e.target.checked)} />
                  <span style={{ fontWeight: 500 }}>研修中として登録する</span>
                </label>
              </div>
              {data.training_enabled && (
                <div className="grid-2" style={{ marginBottom: 12 }}>
                  <div className="form-group">
                    <label>研修日給（円）</label>
                    <input type="number" value={data.training_daily_rate}
                      onChange={(e) => set('training_daily_rate', e.target.value)} />
                    {errors.training_daily_rate && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.training_daily_rate}</div>}
                  </div>
                  <div className="form-group">
                    <label>研修修了目安（出勤日数）</label>
                    <input type="number" value={data.training_days_required}
                      onChange={(e) => set('training_days_required', e.target.value)} />
                    {errors.training_days_required && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.training_days_required}</div>}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>導入メモ（任意・管理者のみ）</label>
                <input value={data.onboarding_note}
                  onChange={(e) => set('onboarding_note', e.target.value)}
                  placeholder="紹介経路・特記事項など" />
              </div>
            </div>
          )}

          {/* ── Step 4: 日報・シフト設定 ── */}
          {step === 4 && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 16 }}>
                各機能の対象有無を設定します。後からいつでも変更できます。
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {([
                  {
                    key: 'report_enabled' as const,
                    label: '日報対象にする',
                    desc: '日報リマインダー・日報一覧の対象になります',
                  },
                  {
                    key: 'shift_enabled' as const,
                    label: 'シフト希望提出対象にする',
                    desc: 'シフト希望の母数・催促通知の対象になります',
                  },
                ] as { key: 'report_enabled' | 'shift_enabled'; label: string; desc: string }[]).map(({ key, label, desc }) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={data[key]}
                      onChange={(e) => set(key, e.target.checked)}
                      style={{ marginTop: 3 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{desc}</div>
                    </div>
                  </label>
                ))}
              </div>

              {!data.payroll_enabled && (
                <div style={{
                  marginTop: 16, padding: '10px 14px',
                  background: 'var(--blue-light)', border: '1px solid rgba(30,95,168,0.2)',
                  borderRadius: 6, fontSize: 12, color: 'var(--ink-2)',
                }}>
                  ℹ 「給与・シフト計算対象」はOFFです（Step 3）。<br/>
                  シフト希望をONにしても月次支払計算の母数には含まれません。
                </div>
              )}
            </div>
          )}

          {/* ── Step 5: 初回案内文 ── */}
          {step === 5 && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
                登録後、以下の文面をLINEやメールでアシスタントに送ってください。
                コピーして使えます。
              </div>
              {!data.login_enabled && (
                <div style={{
                  padding: '10px 14px',
                  background: 'var(--amber-light)', border: '1px solid rgba(176,122,18,0.25)',
                  borderRadius: 6, fontSize: 12, marginBottom: 12, color: 'var(--ink-2)',
                }}>
                  ⚠ ログインアカウントは作成されません。パスワード欄は「（別途お伝えします）」と表示されます。
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>LINE・メール用案内文</div>
                <button className="btn btn-outline btn-sm"
                  onClick={() => copyText(welcomeMsg, 'step5')}>
                  {copyDone === 'step5' ? '✓ コピー済' : 'コピー'}
                </button>
              </div>
              <pre style={{
                background: 'var(--paper-2)', borderRadius: 6, padding: '12px 14px',
                fontSize: 12, lineHeight: 1.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 320, overflowY: 'auto', color: 'var(--ink-2)',
                border: '1px solid var(--paper-3)',
              }}>
                {welcomeMsg}
              </pre>
            </div>
          )}

          {/* ── Step 6: 確認 ── */}
          {step === 6 && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>
                以下の内容でアシスタントを追加します。確認してください。
              </div>
              <div className="card" style={{ marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {(
                      [
                        ['氏名',        data.name + (data.display_name ? ` (${data.display_name})` : '')],
                        ['メールアドレス', data.email + (isProvisional ? ' 【仮登録】' : '')],
                        ['電話・連絡先',  [data.phone, data.contact_note].filter(Boolean).join(' / ') || '—'],
                        ['ログイン',      data.login_enabled
                          ? (data.generate_password ? `あり（初期PW: ${data.initial_password}）` : 'あり')
                          : 'なし'],
                        ['役割', { general: '全般', background: '背景', finishing: '仕上げ', ai_art: 'AI作画', research: '資料' }[data.role_type] ?? data.role_type],
                        ['基本日給',     `¥${Number(data.daily_rate).toLocaleString('ja-JP')}`],
                        ['交通費上限',   `¥${Number(data.transport_cap).toLocaleString('ja-JP')}`],
                        ['研修',         data.training_enabled
                          ? `研修中（日給 ¥${Number(data.training_daily_rate).toLocaleString('ja-JP')} / 目安 ${data.training_days_required}日）`
                          : 'なし'],
                        ['給与対象',     data.payroll_enabled ? 'はい' : 'いいえ（日報専用）'],
                        ['日報対象',     data.report_enabled  ? 'はい' : 'いいえ'],
                        ['シフト対象',   data.shift_enabled   ? 'はい' : 'いいえ'],
                      ] as [string, string][]
                    ).map(([label, value]) => (
                      <tr key={label} style={{ borderTop: '1px solid var(--paper-3)' }}>
                        <td style={{
                          padding: '7px 12px', width: 130,
                          color: 'var(--ink-3)', fontWeight: 500, fontSize: 12,
                          whiteSpace: 'nowrap',
                        }}>{label}</td>
                        <td style={{ padding: '7px 12px', fontWeight: label === '氏名' ? 600 : 400 }}>
                          {value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {errors.submit && (
                <div style={{
                  color: 'var(--red)', fontSize: 12, marginBottom: 12,
                  background: 'var(--red-light)', borderRadius: 6, padding: '8px 12px',
                }}>
                  ⚠ {errors.submit}
                </div>
              )}
            </div>
          )}

        </div>

        {/* フッター */}
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={step === 1 ? onClose : goBack}>
            {step === 1 ? 'キャンセル' : '← 戻る'}
          </button>
          {step < 6 ? (
            <button className="btn btn-primary" onClick={goNext}>
              次へ →
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? '登録中...' : 'この内容でアシスタントを追加'}
            </button>
          )}
        </div>

      </div>
    </div>
  )
}
